import { Pencil, Trash2 } from 'lucide-react'
import { FormProvider } from 'react-hook-form'
import { useIpBlocksTable } from '@/hooks/features/ip-block/useIpBlocksTable'
import { useDataTable } from '@/hooks/ui'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ColumnVisibilityDropdown, DataTable, NoData } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination-controls'
import { Switch } from '@/components/ui/switch'
import { ActionDropdown, ConfirmDialog, PageHeader } from '@/components/atoms'
import { IpBlockModel } from '@/types'
import { IpBlockFilters } from './IpBlockFilters'
import { IpBlockDialog } from './IpBlockDialog'

type TableColumns = 'ip' | 'status' | 'created_at' | 'actions'

const DEFAULT_VISIBLE_COLUMNS: TableColumns[] = [
    'ip',
    'status',
    'created_at',
    'actions',
]

export const IpBlocksTable = () => {
    const {
        loading,
        data,
        pagination,
        formMethods,
        stats,
        mode,
        ipBlockDialogOpen,
        editIpBlock,
        deleteDialogOpen,
        ipBlockToDelete,
        createLoading,
        updateLoading,
        deleteLoading,
        setPage,
        setPageSize,
        openCreateDialog,
        openEditDialog,
        closeDialog,
        handleDeleteClick,
        handleDeleteConfirm,
        setDeleteDialogOpen,
        handleCreateIpBlock,
        handleUpdateIpBlock,
        toggleStatus,
    } = useIpBlocksTable()

    const { columns, changeColumnVisibility, disabledColumns } = useDataTable<
        IpBlockModel.IpBlock,
        TableColumns
    >({
        columns: [
            {
                accessorKey: 'ip',
                columnLabel: () => 'IP address',
                cell: (data) => <span className="font-mono font-medium text-ink">{data?.ip}</span>,
                hasSorting: true,
            },
            {
                accessorKey: 'status',
                columnLabel: () => 'Status',
                cell: (data) => (
                    <div className="flex items-center gap-2">
                        <Badge tone={data?.status === 'active' ? 'error' : 'soft'}>
                            {data?.status === 'active' ? 'Blocked' : 'Inactive'}
                        </Badge>
                        <Switch
                            checked={data?.status === 'active'}
                            onCheckedChange={() => toggleStatus(data)}
                        />
                    </div>
                ),
            },
            {
                accessorKey: 'created_at',
                columnLabel: () => 'Blocked',
                cell: (data) => <span className="tabular-nums text-text-2">{new Date(data?.created_at).toLocaleString()}</span>,
            },
            {
                accessorKey: 'actions',
                columnLabel: () => 'Actions',
                cell: (data) => (
                    <div className="flex justify-end">
                        <ActionDropdown
                            items={[
                                {
                                    label: 'Edit',
                                    icon: Pencil,
                                    onClick: () => openEditDialog(data),
                                },
                                {
                                    label: 'Unblock',
                                    icon: Trash2,
                                    variant: 'destructive',
                                    onClick: () => handleDeleteClick(data),
                                },
                            ]}
                        />
                    </div>
                ),
                classes: () => ({
                    column: 'text-right',
                    cell: 'text-right',
                }),
                isAction: true,
            },
        ],
        visibleColumns: DEFAULT_VISIBLE_COLUMNS,
        disabledColumns: ['actions'],
    })

    return (
        <div>
            <PageHeader
                size="large"
                title="Security"
                description="Block IP addresses from reaching your store."
                onAddClick={openCreateDialog}
                addButtonText="Block IP"
            />

            <Card>
                <CardHeader>
                    <CardTitle>Blocked IP addresses</CardTitle>
                    <CardDescription className="text-text-2">
                        {stats.totalIpBlocks} {stats.totalIpBlocks === 1 ? 'address' : 'addresses'},{' '}
                        {stats.activeIpBlocks} blocked, {stats.inactiveIpBlocks} inactive
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                            <Pagination.PageSizeDropdown
                                pageSize={pagination.pageSize}
                                onPageSizeChange={setPageSize}
                            />
                            <FormProvider {...formMethods}>
                                <IpBlockFilters />
                            </FormProvider>
                        </div>
                        <ColumnVisibilityDropdown
                            columns={columns}
                            onColumnVisibilityChange={changeColumnVisibility}
                            disabledColumns={disabledColumns}
                        />
                    </div>
                    {!loading && data.length === 0 ? (
                        <NoData
                            title="No blocked IPs found"
                            btnText="Block IP"
                            openCreateDialog={openCreateDialog}
                        />
                    ) : (
                        <>
                            <DataTable columns={columns} data={data} loading={loading} />
                            <Pagination.Buttons
                                currentPage={pagination.currentPage}
                                totalItems={pagination.totalCount}
                                pageSize={pagination.pageSize}
                                onPageChange={setPage}
                            />
                        </>
                    )}
                </CardContent>
            </Card>

            <IpBlockDialog
                open={ipBlockDialogOpen}
                mode={mode}
                editIpBlock={editIpBlock}
                closeDialog={closeDialog}
                onCreate={handleCreateIpBlock}
                onUpdate={handleUpdateIpBlock}
                loading={createLoading || updateLoading}
            />

            <ConfirmDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                title="Unblock IP address?"
                description={`This will allow the IP address "${ipBlockToDelete?.ip}" to access your store again.`}
                confirmText={deleteLoading ? 'Unblocking...' : 'Unblock'}
                onConfirm={handleDeleteConfirm}
                loading={deleteLoading}
                variant="destructive"
            />
        </div>
    )
}
